// Small controls shared by the desk pages, drawn from the same tokens as the
// rest of the shell so a button on Reminders looks like a button on Documents.
import type { ChangeEvent, CSSProperties, ReactNode } from 'react';

import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE, v } from 'src/front-components/theme/shell-tokens';

export const DeskButton = ({
  scheme,
  onClick,
  children,
  primary = false,
  disabled = false,
  title,
}: {
  scheme: string;
  onClick: () => void;
  children: ReactNode;
  primary?: boolean;
  disabled?: boolean;
  title?: string;
}) => {
  const shell = getShellColors(scheme);

  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]}`,
        borderRadius: SHELL_RADIUS.sm,
        border: primary ? 'none' : `1px solid ${shell.borderMedium}`,
        background: primary ? v('color-blue') : shell.background,
        color: primary ? v('font-color-inverted') : shell.text,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'default' : 'pointer',
        ...SHELL_TYPE.body,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
};

const fieldStyle = (scheme: string): CSSProperties => {
  const shell = getShellColors(scheme);

  return {
    padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]}`,
    borderRadius: SHELL_RADIUS.sm,
    border: `1px solid ${shell.borderMedium}`,
    background: shell.background,
    color: shell.text,
    ...SHELL_TYPE.body,
    minWidth: 0,
    boxSizing: 'border-box',
  };
};

export const DeskInput = ({
  scheme,
  value,
  onChange,
  placeholder,
  type = 'text',
  width,
}: {
  scheme: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'date' | 'datetime-local';
  width?: string;
}) => (
  <input
    type={type}
    value={value}
    placeholder={placeholder}
    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    style={{ ...fieldStyle(scheme), width: width ?? '100%', maxWidth: '100%' }}
  />
);

export const DeskSelect = ({
  scheme,
  value,
  onChange,
  options,
  width,
}: {
  scheme: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  width?: string;
}) => (
  <select
    value={value}
    onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
    style={{ ...fieldStyle(scheme), width: width ?? '100%' }}
  >
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

/** "Updated 3:42 PM" -- tells the user the page is live and when it last looked. */
export const LiveStamp = ({ scheme, at }: { scheme: string; at: Date | null }) => null;

export const ErrorLine = ({ scheme, message }: { scheme: string; message: string | null }) => {
  if (message === null) return null;

  return (
    <div style={{ ...SHELL_TYPE.caption, color: v('color-red'), padding: `${SHELL_SPACE[1]} 0` }}>{message}</div>
  );
};
